import { styled } from "goober";
import { Fragment, type ComponentProps } from "react";
import { useIsMobile } from "../../hooks/useIsMobile";
import { Divider, RowButton, SearchableDropdown } from "../SearchableDropdown";
import { Icon } from "../Icon";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";

export type ChatAction = {
  icon: ComponentProps<typeof Icon>["name"];
  label: string;
  onClick: () => void;
};

type ChatActionsInput = {
  onRename?: (() => void) | undefined;
  onFork?: (() => void) | undefined;
  onDownload?: (() => void) | undefined;
  onClose?: (() => void) | undefined;
  onTogglePin?: (() => void) | undefined;
  pinned?: boolean;
};

export function chatActionGroups({
  onRename,
  onFork,
  onDownload,
  onClose,
  onTogglePin,
  pinned,
}: ChatActionsInput): { primary: ChatAction[]; secondary: ChatAction[] } {
  const primary: ChatAction[] = [];
  if (onRename) {
    primary.push({ icon: "pencil", label: "Rename chat", onClick: onRename });
  }
  if (onTogglePin) {
    primary.push({
      icon: pinned ? "pin-filled" : "pin",
      label: pinned ? "Unpin chat" : "Pin chat",
      onClick: onTogglePin,
    });
  }
  if (onFork) {
    primary.push({ icon: "copy", label: "Duplicate chat", onClick: onFork });
  }
  if (onDownload) {
    primary.push({
      icon: "circle-arrow-down",
      label: "Download chat",
      onClick: onDownload,
    });
  }
  const secondary: ChatAction[] = [];
  if (onClose) {
    secondary.push({ icon: "x", label: "Close chat", onClick: onClose });
  }
  return { primary, secondary };
}

const Opener = styled("span")`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 9999px;
  color: var(--text-primary);
  cursor: pointer;
  &:hover,
  [aria-expanded="true"] & {
    background: var(--alpha4);
  }
`;

export function ChatActionsMenu(
  props: ChatActionsInput & { disabled?: boolean }
) {
  const isMobile = useIsMobile();
  const { primary, secondary } = chatActionGroups(props);
  if (primary.length === 0 && secondary.length === 0) return null;

  const groups = [primary, secondary].filter((group) => group.length > 0);

  return (
    <SearchableDropdown
      aria-label="Chat actions"
      searchable={false}
      disabled={props.disabled}
      margin={6}
      side={isMobile ? "right" : undefined}
      opener={
        <Tooltip content="Chat actions" side={isMobile ? "right" : "left"}>
          <Opener>
            <Icon name="more" size={12} />
          </Opener>
        </Tooltip>
      }
    >
      {(_query, close) =>
        groups.map((group, index) => (
          <Fragment key={group[0]!.label}>
            {index > 0 && <Divider />}
            {group.map((action) => (
              <RowButton
                key={action.label}
                onClick={() => {
                  action.onClick();
                  close();
                }}
              >
                <Icon name={action.icon} size={16} />
                <Text variant="text-regular">{action.label}</Text>
              </RowButton>
            ))}
          </Fragment>
        ))
      }
    </SearchableDropdown>
  );
}
